import type {
	QueriesOptions,
	QueriesResults,
	UseQueryOptions,
} from '@tanstack/react-query';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useConnection } from '@gov-nx/core/hooks/Connection/setUseConnection';
import { propEq } from '@gov-nx/core/types/helpers.types';
import type { ResponseError } from '../errors/errors.types';
import { usePoResponseError } from '../errors/usePoResponseError';
import type { PoQueryConfig } from './types';

// useQueries is typed as any[] too
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const usePoQueries = <T extends any[]>({
	queries,
	context,
	configs,
	onAllSuccess,
}: {
	queries: QueriesOptions<T>;
	context?: UseQueryOptions['context'];
	onAllSuccess?: () => void;
	configs?: PoQueryConfig[];
}): QueriesResults<T> => {
	const { handleError } = usePoResponseError();
	const queryClient = useQueryClient();
	const { isOffline } = useConnection();

	const requests = useQueries<T>({
		context,
		queries: queries.map((query, index) => {
			const config = configs?.[index];
			return {
				...query,
				onError: (error: AxiosError<ResponseError>) => {
					// Note: Don't throw errors when no internet connection
					if (isOffline) return;

					if (config?.errorIgnoreFilter && config.errorIgnoreFilter(error)) {
						return;
					}

					handleError(error);

					query.onError?.(error);
				},
			};
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		}) as any,
	});

	const allFinished = requests.every(propEq('isSuccess', true));

	useEffect(() => {
		if (allFinished) {
			queries.forEach((query) => {
				const cached =
					query.queryKey && queryClient.getQueryData(query.queryKey);
				if (query.onSuccess && cached) {
					query.onSuccess(cached);
				}
			});
			onAllSuccess?.();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [allFinished]);

	return requests;
};
